import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import HowSteps from "./components/HowSteps";
import LegalFaq from "./components/LegalFaq";

const trustItems = [
  { icon: "ri-shield-check-line", color: "text-secondary", bg: "bg-emerald-50", title: "100% Legal", desc: "Offizielle GGL-Lizenz vom 28. Juni 2024" },
  { icon: "ri-scales-3-line", color: "text-sky-500", bg: "bg-sky-50", title: "Notariell beglaubigt", desc: "Dr. Hans-Peter Schmidt überwacht jede Ziehung" },
  { icon: "ri-lock-line", color: "text-violet-500", bg: "bg-violet-50", title: "Sicher bezahlen", desc: "256-Bit-SSL-Verschlüsselung, PCI-DSS zertifiziert" },
  { icon: "ri-heart-line", color: "text-rose-500", bg: "bg-rose-50", title: "Guter Zweck", desc: "10% von jedem Los gehen an gemeinnützige Vereine" },
  { icon: "ri-award-line", color: "text-primary", bg: "bg-amber-50", title: "98 Gewinner", desc: "Alle Preise wurden seit 2022 erfolgreich übergeben" },
  { icon: "ri-customer-service-2-line", color: "text-primary", bg: "bg-orange-50", title: "Support 7/24", desc: "Unser Team antwortet in weniger als 2 Stunden" },
];

export default function CommentPage() {
  return (
    <main className="min-h-screen font-inter bg-white">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden pt-24 pb-20">
        {/* BG image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stunning%20luxury%20villa%20aerial%20view%20with%20swimming%20pool%20and%20garden%2C%20bright%20sunny%20day%2C%20exclusive%20estate%2C%20beautiful%20from%20above%2C%20architectural%20drone%20photography%2C%20clean%20sky%2C%20warm%20golden%20tones%2C%20high%20quality%20real%20estate&width=1600&height=700&seq=comment-hero-bg&orientation=landscape"
            alt="Villa"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/70 to-white"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm text-white/60 mb-8">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Startseite</Link>
            <i className="ri-arrow-right-s-line text-white/30"></i>
            <span className="text-white/90 font-medium">Wie es funktioniert</span>
          </nav>

          <span className="inline-flex items-center gap-2 bg-secondary/20 border border-secondary-light/30 text-emerald-300 text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest mb-6">
            <i className="ri-information-line"></i>
            Vollständiger Guide
          </span>

          <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white leading-tight mb-5">
            Wie es funktioniert
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto leading-relaxed mb-10">
            Entdecken Sie in 6 einfachen Schritten, wie Sie an der zertifizierten Immobilien-Verlosung teilnehmen und möglicherweise Ihre Traumvilla gewinnen können.
          </p>

          {/* Quick nav */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href="#steps" className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-6 py-3 rounded-full hover:bg-slate-100 transition-all cursor-pointer whitespace-nowrap text-sm">
              <i className="ri-list-ordered-2"></i>
              Die 6 Schritte
            </a>
            <a href="#faq" className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/25 text-white font-semibold px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap text-sm backdrop-blur-sm">
              <i className="ri-question-line"></i>
              Rechtliche FAQ
            </a>
            <Link to="/forfaits" className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap text-sm shadow-lg">
              <i className="ri-ticket-2-line"></i>
              Teilnehmen
            </Link>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ── */}
      <section className="border-y border-slate-100 bg-slate-50 py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {trustItems.map((t, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2">
                <div className={`w-11 h-11 ${t.bg} rounded-xl flex items-center justify-center`}>
                  <i className={`${t.icon} text-lg ${t.color}`}></i>
                </div>
                <p className="font-bold text-slate-900 text-xs">{t.title}</p>
                <p className="text-slate-400 text-[10px] leading-tight">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STEPS ── */}
      <div id="steps">
        <HowSteps />
      </div>

      {/* ── TIMELINE SUMMARY ── */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="uppercase text-secondary-light text-xs font-bold tracking-widest">Auf einen Blick</span>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mt-2">
              Vom Kauf bis zur Schlüsselübergabe
            </h2>
          </div>
          <div className="flex flex-col md:flex-row items-start gap-0 md:gap-0 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-5 left-0 right-0 h-px bg-white/10 z-0" style={{ top: "1.25rem" }}></div>
            {[
              { icon: "ri-cursor-line", label: "Kauf der Lose", time: "Tag 0", color: "text-primary-light" },
              { icon: "ri-mail-check-line", label: "E-Mail Erhalt", time: "Tag 0 + 2 Min", color: "text-secondary" },
              { icon: "ri-live-line", label: "Live-Ziehung", time: "Ziehungsdatum", color: "text-rose-400" },
              { icon: "ri-phone-line", label: "Gewinner-Kontakt", time: "Tag +1", color: "text-violet-400" },
              { icon: "ri-file-text-line", label: "Notartermin", time: "Tag +15", color: "text-primary-light" },
              { icon: "ri-key-line", label: "Schlüsselübergabe", time: "Max. Tag +30", color: "text-secondary-light" },
            ].map((item, i) => (
              <div key={i} className="relative z-10 flex-1 flex flex-col items-center text-center gap-2 py-4 px-2">
                <div className="w-10 h-10 bg-white/10 border border-white/20 rounded-full flex items-center justify-center">
                  <i className={`${item.icon} ${item.color} text-base`}></i>
                </div>
                <p className="text-white text-xs font-semibold leading-tight">{item.label}</p>
                <span className={`text-[10px] font-bold uppercase tracking-wide ${item.color}`}>{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <LegalFaq />

      {/* ── CTA FINAL ── */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="uppercase text-primary text-xs font-bold tracking-widest">Bereit, Ihr Glück zu versuchen?</span>
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-4">
            Sie wissen jetzt alles —<br />
            <em className="text-slate-400 font-normal">jetzt sind Sie dran</em>
          </h2>
          <p className="text-slate-500 leading-relaxed mb-8">
            Verlosung #52 · 15. April 2025 · 500 m² Villa — <strong className="text-primary">1.250.000 €</strong><br />
            Nur noch <strong>618 Lose</strong> verfügbar vor Verkaufsende.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/forfaits"
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold px-10 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap shadow-xl hover:-translate-y-0.5 text-base"
            >
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-ticket-2-line text-lg"></i></span>
              Meine Lose kaufen
            </Link>
            <Link
              to="/bien"
              className="inline-flex items-center gap-2 border-2 border-slate-200 text-slate-700 hover:border-slate-300 font-semibold px-8 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap text-base"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-home-4-line"></i></span>
              Villa ansehen
            </Link>
          </div>
          <p className="text-slate-400 text-xs mt-6 flex items-center justify-center gap-1.5">
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-shield-check-line text-secondary text-xs"></i></span>
            Sichere Zahlung · Notariell beglaubigte Ziehung · Nur ab 18 Jahren
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
